/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { List, AutoSizer } from "react-virtualized";
import { Typography, makeStyles, IconButton } from "@material-ui/core";
import _ from "lodash";
import {
  TiArrowUnsorted,
  TiArrowSortedUp,
  TiArrowSortedDown,
} from "react-icons/ti";
import { isCycleTypeById } from "lib/utils/cycles";
import {
  SellOut,
  SellIn,
  SalesOut,
  SalesIn,
  InvestmentIn,
  InvestmentOut,
  PriceOut,
  PriceIn,
} from "resources/constants";
import { sortEstimationsByParams } from "lib/utils/estimations";
import { useRouteMatch } from "react-router-dom";
import EstimationRowComponent from "./EstimationRowComponent";
import EstimationRowDailyPromotionsComponent from "./EstimationRowDailyPromotionsComponent";
import EstimationRowTradePromotionAnnualComponent from "./EstimationRowTradePromotionAnnualComponent";

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    display: "flex",
    backgroundColor: theme.palette.primary.main,
    justifyContent: "space-evenly",
  },
  rowHeaderCell: {
    display: "flex",
    flexFlow: "column",
    color: theme.palette.common.white,
    justifyContent: "flex-start",
    padding: "15px 5px 15px 5px",
    width: "145px",
    minWidth: "145px",
    "& >div:first-of-type": {
      display: "flex",
    },
    "&:first-child": {
      flexBasis: "50px",
      maxWidth: "50px",
      minWidth: "50px",
    },
  },
  rowHeaderUnits: {
    fontSize: "11px",
  },
  numeric: {
    alignItems: "flex-end",
  },
  large: {
    width: "145px",
    minWidth: "145px",
  },
  virtualizedList: {
    outline: "none",
    border: "1px solid #e2e2e2",
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },

    /* Handle */
    "&::-webkit-scrollbar-thumb": {
      background: "#bbb",
      borderRadius: "5px",
      padding: "1px",
    },

    /* Handle on hover */
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#999",
    },
  },
  sortingArrows: {
    display: "flex",
  },
  selectedSortingMethod: {
    color: "white",
    opacity: "1",
  },
  notSelectedSortingMethod: {
    color: "white",
    opacity: "0.2",
    "&:hover": {
      opacity: "1",
    },
  },
  withLeftMargin: {
    marginLeft: "10px",
  },
  withRightMargin: {
    marginRight: "10px",
  },
}));

export default function EstimationsListComponent({
  scenarioEstimations,
  referenceEstimations,
  groupBy,
}) {
  const { url } = useRouteMatch();
  const isTpo = !url.includes("pricing");
  const labelTableColumns = ["product", "retailer", "area"];
  const numericTableColumns = isTpo
    ? ["price", "units", "sales", "margin", "units_in", "investment_in"]
    : ["price", "units", "sales", "margin", "units_in", "sales_in"];

  const normalCellWidth = 150;
  const largeCellWidth = 150;
  const scenariosSlice = useSelector((state) => state.scenarios);
  const cycleSlice = useSelector((state) => state.cycle);
  const filtersSlice = useSelector((state) => state.filters);
  const metadataSlice = useSelector((state) => state.metadata);
  const classes = useStyles();
  const tableRef = useRef();
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const sellOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellOut
    : "Sell Out";
  const sellInLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellIn
    : "Sell In";
  const salesOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SalesOut
    : "Sales Out";
  const salesInLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SalesIn
    : "Sales In";
  const investmentInLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? InvestmentIn
    : "Investment In";
  const investmentOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? InvestmentOut
    : "Investment Out";
  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";
  const priceInLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceIn
    : "Price In";
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [minTableWidth, setMinTableWidth] = useState(
    50 + largeCellWidth * 3 + normalCellWidth * 6
  );
  const [tableWidth, setTableWidth] = useState(null);
  const [expandedRow, setExpandedRow] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPriceChange, setIsLoadingPriceChange] = useState(false);
  const [sortedEstimations, setSortedEstimations] = useState(
    sortEstimationsByParams(
      scenarioEstimations,
      referenceEstimations,
      "units",
      "desc",
      configCatSlice?.features.newInvestmentCalculation
    )
  );
  const [dailyPromotion, setDailyPromotion] = useState(false);
  const [sortingParams, setSortingParams] = useState({
    value: "units",
    order: "desc",
  });

  const checkTradePromotionAnnual = () => {
    return (
      isCycleTypeById(
        scenariosSlice.selectedScenario.scenario.cycle.cycle_type_id,
        cycleSlice
      ) === "Pricing 2.0"
    );
  };

  const checkDailyPromotion = () => {
    return (
      isCycleTypeById(
        scenariosSlice.selectedScenario.scenario.cycle.cycle_type_id,
        cycleSlice
      ) === "Daily Trade Promotions"
    );
  };

  const isDailyPromotions = checkDailyPromotion();
  const dailyPromotionsOrTpo = isDailyPromotions ? 715 : 585;
  const valueHeight = checkTradePromotionAnnual() ? 685 : dailyPromotionsOrTpo;

  const rowHeight = ({ index }) =>
    index === selectedIndex && expandedRow !== -1 ? valueHeight : 61;

  const rowRenderer = ({ index, key, style, parent }) => {
    if (
      expandedRow !== -1 &&
      !(
        expandedRow >= parent._renderedRowStartIndex &&
        expandedRow <= parent._renderedRowStopIndex
      )
    ) {
      setExpandedRow(-1);
    }
    const currentRow = sortedEstimations[index];
    const isTradePromotionAnnual = checkTradePromotionAnnual();
    const referenceRow =
      referenceEstimations[
        `${currentRow.product_id}-${currentRow.area_id}-${currentRow.retailer_id}`
      ];

    if (isTradePromotionAnnual) {
      return referenceRow ? (
        <EstimationRowTradePromotionAnnualComponent
          isReference={
            scenariosSlice.selectedScenario.scenario.id ===
            scenariosSlice.referenceScenario.scenario.id
          }
          isTradePromotionAnnual={isTradePromotionAnnual}
          groupBy={groupBy}
          key={key}
          style={style}
          row={currentRow}
          referenceRow={referenceRow}
          index={index}
          setSelectedIndex={setSelectedIndex}
          expandedRow={expandedRow}
          tableWidth={tableWidth}
          setExpandedRow={setExpandedRow}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isLoadingPriceChange={isLoadingPriceChange}
          setIsLoadingPriceChange={setIsLoadingPriceChange}
          isBlocked={scenariosSlice.selectedScenario?.scenario?.is_blocked}
        />
      ) : null;
    }

    if (isDailyPromotions) {
      return referenceRow ? (
        <EstimationRowDailyPromotionsComponent
          isReference={
            scenariosSlice.selectedScenario.scenario.id ===
            scenariosSlice.referenceScenario.scenario.id
          }
          isTradePromotionAnnual={isTradePromotionAnnual}
          groupBy={groupBy}
          key={key}
          style={style}
          row={currentRow}
          referenceRow={referenceRow}
          index={index}
          setSelectedIndex={setSelectedIndex}
          expandedRow={expandedRow}
          tableWidth={tableWidth}
          setExpandedRow={setExpandedRow}
          setDailyPromotion={setDailyPromotion}
          dailyPromotion={dailyPromotion}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isBlocked={scenariosSlice.selectedScenario?.scenario?.is_blocked}
        />
      ) : null;
    }

    return referenceRow ? (
      <EstimationRowComponent
        isReference={
          scenariosSlice.selectedScenario.scenario.id ===
          scenariosSlice.referenceScenario.scenario.id
        }
        isTradePromotionAnnual={isTradePromotionAnnual}
        groupBy={groupBy}
        key={key}
        style={style}
        row={currentRow}
        referenceRow={referenceRow}
        index={index}
        setSelectedIndex={setSelectedIndex}
        expandedRow={expandedRow}
        tableWidth={tableWidth}
        setExpandedRow={setExpandedRow}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isBlocked={scenariosSlice.selectedScenario?.scenario?.is_blocked}
      />
    ) : null;
  };

  const sortEstimations = (value, order) => {
    setSortingParams({ value, order });
    setSortedEstimations(
      sortEstimationsByParams(
        scenarioEstimations,
        referenceEstimations,
        value,
        order,
        configCatSlice?.features.newInvestmentCalculation
      )
    );
  };

  useMemo(() => {
    const orderedEstimations = sortEstimationsByParams(
      scenarioEstimations,
      referenceEstimations,
      sortingParams.value,
      sortingParams.order,
      configCatSlice?.features.newInvestmentCalculation
    );
    setSortedEstimations(orderedEstimations);
  }, [filtersSlice.filters]);

  const getTotalTooltip = (label) => {
    switch (label) {
      case "Sales-Out":
        return salesOutLabelByTenant;
      case "Sales-In":
        return salesInLabelByTenant;
      case "Investment-Out":
        return investmentOutLabelByTenant;
      case "Investment-In":
        return investmentInLabelByTenant;
      case "Margin":
        return "Margin";
      case "Price-Out":
        return priceOutLabelByTenant;
      case "Price-In":
        return priceInLabelByTenant;
      case "Sell-Out":
        return sellOutLabelByTenant;
      case "Sell-In":
        return isTpo ? "ROI" : sellInLabelByTenant;
      default:
        return "";
    }
  };

  useMemo(() => {
    setDailyPromotion(false);
    if (sortedEstimations[expandedRow]) {
      const modifiedRowIndex = _.findIndex(
        sortedEstimations,
        (estimation) =>
          estimation.product_id === sortedEstimations[expandedRow].product_id &&
          estimation.area_id === sortedEstimations[expandedRow].area_id &&
          estimation.retailer_id === sortedEstimations[expandedRow].retailer_id
      );
      setSelectedIndex(modifiedRowIndex);
      setExpandedRow(modifiedRowIndex);
    }
  }, [sortedEstimations]);

  useMemo(() => {
    const orderedEstimations = sortEstimationsByParams(
      scenarioEstimations,
      referenceEstimations,
      sortingParams.value,
      sortingParams.order,
      configCatSlice?.features.newInvestmentCalculation
    );
    setSortedEstimations(orderedEstimations);

    if (tableRef.current) {
      tableRef.current.recomputeRowHeights();
      tableRef.current.forceUpdateGrid();
    }
  }, [expandedRow, tableRef]);

  useEffect(() => {
    // The reason why we use one variable to handle scrolling and another to handle expansion is that when we change the filters we want to scroll to the top of the page (index 0) without expanding the first row (hence the -1 to specify that no row should be expanded)
    setSelectedIndex(0);
    setExpandedRow(-1);

    if (scenariosSlice.selectedScenario.scenario.scenario_type_id === 1)
      setMinTableWidth(50 + largeCellWidth * 3 + normalCellWidth * 6);
    else {
      setMinTableWidth(50 + largeCellWidth * 3 + normalCellWidth * 7);
    }
  }, [filtersSlice, scenariosSlice.selectedScenario.scenario.id]);

  useMemo(() => {
    setSortedEstimations(
      sortEstimationsByParams(
        scenarioEstimations,
        referenceEstimations,
        "units",
        "desc"
      )
    );
  }, [scenariosSlice.selectedScenario.scenario.cycle_id]);

  return (
    <>
      <div
        className={classes.tableHeader}
        style={{
          width: tableWidth > minTableWidth ? tableWidth : minTableWidth,
        }}
      >
        <div className={classes.rowHeaderCell} />
        {_.map(labelTableColumns, (columnName) => {
          if (groupBy && ["retailer", "area"].includes(columnName)) return null;
          return (
            <div
              className={`${classes.rowHeaderCell} ${classes.large}`}
              key={columnName}
            >
              <div>
                <Typography variant="body2">
                  {columnName.charAt(0).toUpperCase() + columnName.slice(1)}
                </Typography>
                <div
                  className={`${classes.sortingArrows} ${classes.withLeftMargin}`}
                >
                  {sortingParams.value !== `${columnName}.label` && (
                    <IconButton
                      aria-label={`sort ${columnName}`}
                      size="small"
                      onClick={() =>
                        sortEstimations(`${columnName}.label`, "asc")
                      }
                      classes={{
                        root: classes.notSelectedSortingMethod,
                      }}
                    >
                      <TiArrowUnsorted />
                    </IconButton>
                  )}
                  {sortingParams.value === `${columnName}.label` && (
                    <IconButton
                      aria-label={`sort ${columnName}`}
                      size="small"
                      onClick={() =>
                        sortEstimations(
                          `${columnName}.label`,
                          sortingParams.order === "asc" ? "desc" : "asc"
                        )
                      }
                      classes={{
                        root: classes.selectedSortingMethod,
                      }}
                    >
                      {sortingParams.order === "asc" ? (
                        <TiArrowSortedUp />
                      ) : (
                        <TiArrowSortedDown />
                      )}
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {_.map(numericTableColumns, (columnName) => {
          if (
            columnName === "investment_in" &&
            scenariosSlice.selectedScenario.scenario.id ===
              scenariosSlice.referenceScenario.scenario.id &&
            !isTpo
          )
            return null;

          const isInvestmentOrROI =
            columnName === "investment_in" ||
            (columnName === "units_in" && isTpo);
          const tableHeaderDescriptionInvestmentnOrROI = isInvestmentOrROI
            ? ""
            : metadataSlice["custom-projection"][columnName][0].unit;

          return (
            metadataSlice["custom-projection"][columnName][0]
              .visibility_type === 2 && (
              <div
                className={`${classes.rowHeaderCell} ${classes.numeric}`}
                key={columnName}
              >
                <div>
                  <div
                    className={`${classes.sortingArrows} ${classes.withRightMargin}`}
                  >
                    {sortingParams.value !== `${columnName}` && (
                      <IconButton
                        aria-label={`sort ${columnName}`}
                        size="small"
                        onClick={() => sortEstimations(`${columnName}`, "asc")}
                        classes={{
                          root: classes.notSelectedSortingMethod,
                        }}
                      >
                        <TiArrowUnsorted />
                      </IconButton>
                    )}
                    {sortingParams.value === `${columnName}` && (
                      <IconButton
                        aria-label={`sort ${columnName}`}
                        size="small"
                        onClick={() =>
                          sortEstimations(
                            `${columnName}`,
                            sortingParams.order === "asc" ? "desc" : "asc"
                          )
                        }
                        classes={{
                          root: classes.selectedSortingMethod,
                        }}
                      >
                        {sortingParams.order === "asc" ? (
                          <TiArrowSortedUp />
                        ) : (
                          <TiArrowSortedDown />
                        )}
                      </IconButton>
                    )}
                  </div>
                  <Typography variant="body2">
                    {getTotalTooltip(
                      metadataSlice["custom-projection"][columnName][0].label
                    )}
                  </Typography>
                </div>
                <Typography variant="body2" className={classes.rowHeaderUnits}>
                  {tableHeaderDescriptionInvestmentnOrROI}
                </Typography>
              </div>
            )
          );
        })}
      </div>
      {!_.isEmpty(scenarioEstimations) && (
        <AutoSizer>
          {({ width, height }) => {
            setTableWidth(width);
            return (
              <List
                className={classes.virtualizedList}
                scrollToAlignment="start"
                ref={tableRef}
                width={width >= minTableWidth ? width : minTableWidth}
                height={height - 70} // We have to substract the height of the list header
                rowHeight={rowHeight} // 60
                rowCount={sortedEstimations.length}
                rowRenderer={rowRenderer}
                scrollToIndex={selectedIndex}
              />
            );
          }}
        </AutoSizer>
      )}
    </>
  );
}
